'use client';

import {
  ComponentPropsWithoutRef,
  createContext,
  ComponentRef,
  forwardRef,
  HTMLAttributes,
  useContext,
  useId,
} from 'react';

import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';

import { Label } from '@/components/ui/Label';
import { cn } from '@/utils/app.utils';

const Form = FormProvider;

type FormItemContextValue = {
  id: string;
};

const FormItemContext = createContext<FormItemContextValue>(
  {} as FormItemContextValue
);

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName;
};

const FormFieldContext = createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

const FormItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn('space-y-1', className)} {...props} />
      </FormItemContext.Provider>
    );
  }
);

FormItem.displayName = 'FormItem';

const FormLabel = forwardRef<
  ComponentRef<typeof LabelPrimitive.Root>,
  ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      htmlFor={formItemId}
      className={cn(
        { 'text-destructive': !!error },
        'text-sm text-default dark:text-dark-300 font-medium',
        className
      )}
      {...props}
    />
  );
});

FormLabel.displayName = 'FormLabel';

const FormControl = forwardRef<
  ComponentRef<typeof Slot>,
  ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const {
    error, formItemId, formDescriptionId, formMessageId,
  } =
    useFormField();

  return (
    <Slot
      ref={ref}
      aria-invalid={!!error}
      id={formItemId}
      aria-describedby={
        !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`
      }
      {...props}
    />
  );
});

FormControl.displayName = 'FormControl';

export interface IFormDescriptionProps
  extends HTMLAttributes<HTMLParagraphElement> {
  error?: boolean;
}
const FormDescription = forwardRef<HTMLParagraphElement, IFormDescriptionProps>(
  ({ className, error, ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    return (
      <p
        ref={ref}
        id={formDescriptionId}
        className={cn(
          'text-[0.8rem] text-muted-foreground',
          className,
          error && 'text-error dark:text-error-light'
        )}
        {...props}
      />
    );
  }
);

FormDescription.displayName = 'FormDescription';

export interface IFormMessageProps extends HTMLAttributes<HTMLParagraphElement> {
  error?: boolean;
}
const FormMessage = forwardRef<HTMLParagraphElement, IFormMessageProps>(
  ({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : children;

    if (!body) {
      return null;
    }

    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn(
          'text-[0.8rem] font-medium',
          className,
          error ? 'text-error' : 'text-destructive'
        )}
        {...props}
      >
        {body}
      </p>
    );
  }
);

FormMessage.displayName = 'FormMessage';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
